import React from 'react';
import Seo from '../components/Seo';
import { Shield, Lock, Eye, FileText, ChevronRight, Activity, Users, Clock, CheckCircle, Globe, BarChart3, Mail } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const sections = [
    {
      title: "Scope",
      icon: <FileText className="text-cyan-400" size={24} />,
      content: (
        <p className="text-slate-300">This Policy applies to information collected through the Site and related online services that link to this Policy. It does not cover information processed on behalf of hospital or enterprise customers through our platform under a separate agreement (e.g., a services agreement and/or data processing agreement).</p>
      )
    },
    {
      title: "Information We Collect",
      icon: <Eye className="text-cyan-400" size={24} />,
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-bold text-white mb-1">A. Information you provide</h4>
            <p className="text-slate-300">We may collect information you provide directly, such as: Contact details (name, email address, phone number), Professional details (company/organization, role/title), and Inquiry content (messages submitted via forms, scheduling tools, or email).</p>
          </div>
          <div>
            <h4 className="font-bold text-white mb-1">B. Information collected automatically</h4>
            <p className="text-slate-300">When you use the Site, we may automatically collect: Device and network data (IP address, browser type, operating system, device identifiers), Usage data (pages viewed, links clicked, time spent, referring/exit pages), and Approximate location (derived from IP address).</p>
          </div>
          <div>
            <h4 className="font-bold text-white mb-1">C. Cookies and similar technologies</h4>
            <p className="text-slate-300">We use cookies and similar technologies (including analytics and session replay tools such as PostHog, Microsoft Clarity, and rrweb) to understand usage, improve the Site, and troubleshoot issues. These tools may record interactions like clicks, scrolling, and page navigation.</p>
          </div>
        </div>
      )
    },
    {
      title: "How We Use Information",
      icon: <Activity className="text-cyan-400" size={24} />,
      content: (
        <ul className="list-disc pl-5 space-y-1 text-slate-300">
          <li>Provide and operate the Site</li>
          <li>Respond to inquiries and provide requested information</li>
          <li>Improve and secure the Site and user experience</li>
          <li>Analyze performance and usage to enhance functionality</li>
          <li>Communicate with you about DeltaOps</li>
          <li>Comply with legal obligations and enforce our terms</li>
        </ul>
      )
    },
    {
      title: "Legal Bases (EEA/UK)",
      icon: <Shield className="text-cyan-400" size={24} />,
      content: (
        <ul className="list-disc pl-5 space-y-1 text-slate-300">
          <li>Consent (e.g., non-essential cookies where required)</li>
          <li>Legitimate interests (e.g., Site analytics, security)</li>
          <li>Contract (e.g., taking steps at your request)</li>
          <li>Legal obligations (e.g., compliance, recordkeeping)</li>
        </ul>
      )
    },
    {
      title: "How We Share Information",
      icon: <Users className="text-cyan-400" size={24} />,
      content: (
        <div>
          <p className="text-slate-300 mb-2">We may share information with service providers, professional advisors, for legal/security reasons, or in connection with corporate transactions. <span className="text-cyan-400 font-bold">We do not sell your personal information.</span></p>
        </div>
      )
    },
    {
      title: "Data Retention",
      icon: <Clock className="text-cyan-400" size={24} />,
      content: <p className="text-slate-300">We retain information only as long as reasonably necessary for the purposes described in this Policy, including to meet legal, accounting, or reporting requirements.</p>
    },
    {
      title: "Security",
      icon: <Lock className="text-cyan-400" size={24} />,
      content: <p className="text-slate-300">We maintain administrative, technical, and physical safeguards designed to protect information. However, no security measure is perfect, and we cannot guarantee absolute security.</p>
    },
    {
      title: "Your Choices and Rights",
      icon: <CheckCircle className="text-cyan-400" size={24} />,
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-bold text-white mb-1">Cookie controls</h4>
            <p className="text-slate-300">Adjust settings via any consent tool or browser controls. Blocking cookies may affect Site functionality.</p>
          </div>
          <div>
            <h4 className="font-bold text-white mb-1">Marketing communications</h4>
            <p className="text-slate-300">Opt out via the unsubscribe link in our emails.</p>
          </div>
          <div>
            <h4 className="font-bold text-white mb-1">Privacy rights</h4>
            <p className="text-slate-300">Depending on jurisdiction, you may request access, correction, deletion, or restriction of your data.</p>
          </div>
        </div>
      )
    },
    {
      title: "International Data Transfers",
      icon: <Globe className="text-cyan-400" size={24} />,
      content: <p className="text-slate-300">We take steps to provide appropriate safeguards as required by law when transferring data across borders.</p>
    },
    {
      title: "Children’s Privacy",
      icon: <Users className="text-cyan-400" size={24} />,
      content: <p className="text-slate-300">The Site is not intended for children under 13. We do not knowingly collect personal information from children.</p>
    },
    {
      title: "Healthcare and HIPAA Notice",
      icon: <Activity className="text-cyan-400" size={24} />,
      content: <p className="text-slate-300">Unless explicitly agreed, information collected via the Site is not intended to include protected health information (PHI). Do not submit PHI through website forms.</p>
    },
    {
      title: "AI-Generated Images",
      icon: <BarChart3 className="text-cyan-400" size={24} />,
      content: <p className="text-slate-300">Some images on the Site may be generated using AI for aesthetic purposes and are not intended to depict real individuals or events.</p>
    }
  ];

  return (
    <div className="bg-slate-950 min-h-screen text-white">
      <Seo 
        title="Privacy Policy | DeltaOps AI" 
        description="Learn about how DeltaOps AI handles your data and protects your privacy while delivering agentic AI for healthcare."
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-slate-900" style={{
        background: `linear-gradient(rgba(2, 12, 27, 0.8), rgba(2, 12, 27, 0.9)), url('https://images.pexels.com/photos/5473302/pexels-photo-5473302.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2') center/cover`
      }}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/50 to-slate-950"></div>
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-cyan-500 rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-600 rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1 mb-6">
            <Shield size={16} className="text-cyan-400" />
            <span className="text-cyan-100 text-sm font-medium">Compliance & Security</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Privacy <span className="text-cyan-400">Policy</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            This Privacy Policy describes how DeltaOps AI (“DeltaOps,” “we,” “us,” or “our”) collects, uses, discloses, and protects information when you visit our website or interact with us online.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-[#020a17]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/5 rounded-2xl shadow-xl overflow-hidden border border-white/10 backdrop-blur-sm">
              <div className="p-8 md:p-12">
                <div className="space-y-12">
                  {sections.map((section, index) => (
                    <div key={index} className="flex flex-col md:flex-row gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-cyan-900/20 rounded-xl flex items-center justify-center border border-cyan-500/30">
                          {section.icon}
                        </div>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white mb-4">{section.title}</h2>
                        <div className="text-lg text-slate-300 leading-relaxed italic">
                          {section.content}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-16 p-8 bg-slate-900/50 rounded-xl border border-white/5">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <ChevronRight className="text-cyan-400 mr-2" />
                    Changes to This Policy
                  </h3>
                  <p className="text-slate-400 mb-6">
                    We may update this Policy from time to time. The “Last Updated” date indicates when the Policy was most recently revised. Your continued use of the Site after changes means you accept the updated Policy.
                  </p>
                  
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Mail className="text-cyan-400 mr-2" size={20} />
                    Contact Us
                  </h3>
                  <p className="text-slate-400">
                    For questions or requests regarding this Policy, contact:<br />
                    <span className="text-white font-bold">DeltaOps AI</span><br />
                    Email: <a href="mailto:hi@deltaops.health" className="text-cyan-400 hover:underline">hi@deltaops.health</a>
                  </p>
                  
                  <p className="mt-8 text-sm text-cyan-400 font-semibold tracking-wider uppercase">
                    Last Updated: February 19, 2026
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
